export default {
  'ATS Tip': {
    title: 'ATS Specific Tips',
    message: `
      <p>Each ATS views and interprets your resume differently. For example, some ATS recognize plural versions of a keyword, while others do not.</p>
      <p>The ATS-specific feature matches the company name and URL of the application link with the ATS that it uses.</p>
      <p>This feature does not yet match all companies, but coverage will continue to grow over time.</p>
    `,
  },
  'Job Title Match': {
    title: 'Job Title Match',
    message: `
    <p>Job title is also an important factor in your match rate. Most ATS will match candidates with exact job titles to the open position.</p>
    <p>If you had a similar position in the past, change or add that job title in your work experience to the one you are applying for. 
    As long as your roles were essentially the same, this tactic is not deceitful. If you haven’t had similar job titles before, 
    you can include it in the summary statement.</p>
    `,
  },
  'Education Match': {
    title: 'Education Match',
    message: `
    <p>This job description requires a degree. If you have a degree that is not an exact match to the job description’s requirement, try adding a brief explanation of your education including the exact language of the job description.</p>
    <i>For Example:</i>
    <p>"M. Tech. in Computer Science" could be paraphrased "M. Tech. (M.Eng) in Computer Science"</p>
    `,
  },
  'Resume Parsing Accuracy': {
    title: 'Resume Parsing Accuracy',
    message: `
    <p>Resume Parsing Accuracy indicates how well Applicant Tracking Systems (ATS) can read and process your original resume content. A lower parsing accuracy often means your resume's formatting may be preventing ATS systems from properly reading your qualifications.</p>
    <p>This percentage reflects the initial parsing of your uploaded resume, not your current Power Edit version which is already formatted for optimal ATS compatibility.</p>
    <p>Common causes of poor parsing include:</p>
    <ul class="list-disc ml-5 mb-2">
        <li>Complex formatting and tables</li>
        <li>Headers/footers</li>
        <li>Non-standard section headings</li>
        <li>Non-standard fonts</li>
        <li>Special characters</li>
        <li>Images and graphics</li>
        <li>PDF format limitations</li>
    </ul>
    <p>Your resume in Power Edit is now using an ATS-optimized template, ensuring maximum parsing accuracy for future applications.</p>
    `,
  },
  'Section Headings': {
    title: 'Section Headings',
    message: `
    <p>Section headings are important to format correctly because ATS will often parse your resume into sections, e.g. Work Experience, Education, Awards/Achievements, etc.</p>
    <p>Make sure your section headings are consistent with industry standards for your information to be read correctly.</p>
    <p>This check does not affect your match rate.</p>
    `,
  },
  'Date Formatting': {
    title: 'Date Formatting',
    message: `
    <p>We recommend keeping the dates for work experience in the following format: “MM / YY or MM / YYYY or Month YYYY” (e.g. 03/18, 03/2019, Mar 2019 or March 2019).</p>
    <p>This check does not affect your match rate.</p>
    `,
  },
  'Contact Information': {
    title: 'Contact Information',
    message: `
    <p>Correct and complete contact information is important in your resume so that recruiters will be able to contact you. 
    While most recruiters use email as the primary means of contact, many recruiters will reach out directly via a phone call. 
    Make sure you provide accurate and up to date contact information in your resume.</p>
    `,
  },
  'File Type': {
    title: 'File Type',
    message: `
    <p>Some file types cause errors in ATS. Based on our research, we recommend using a .docx or .pdf file for your resume.</p>
    <p>This check does not affect your match rate.</p>
    `,
  },
  'Word Count': {
    title: 'Word Count',
    message: `
    <p>Many ATS use something called signal-to-noise ratio, which means that pertinent information is compared to irrelevant information.</p>
    <p>So, using less text can actually help increase your resume’s relevance. As a rule of thumb, aim for a one-page resume if you have zero 
    to 10 years of work experience. If you have more than 10 years of work experience, you can use a two-page resume—but only if you have 
    enough quality content to require two pages.</p>
    <p>If you pad your resume with fluff, a hiring manager might miss important information. If you are in academia, you’ll use a curriculum 
    vitae (CV) instead, which can be much longer. Many countries outside the U.S. have their own resume practices—for example, 
    longer resumes are the norm in Australia.</p>
    `,
  },
  'Hard Skills': {
    title: 'Hard Skills',
    message: `
      <p>Hard skills enable you to perform job-specific duties and responsibilities. You can learn hard skills in the classroom, training courses, and on the job. These skills are typically focused on teachable tasks and measurable abilities such as the use of tools, equipment, or software. Hard skills have a high impact on your match score.</p>
      <p>
        <strong>Tip:</strong>
        <span> Match the skills in your resume to the exact spelling in the job description. Prioritize skills that appear most frequently in the job description.</span>
      </p>
    `,
  },
  'Soft Skills': {
    title: 'Soft Skills',
    message: `
      <p>Soft skills are your traits and abilities that are not unique to any job. Your soft skills are part of your personality, and can be learned also. These skills are the traits that typically make you a good employee for any company such as time management and communication. Soft skills have a medium impact on your match score.</p>
      <p>
        <strong>Tip:</strong>
        <span> Prioritize hard skills in your resume to get interviews, and then showcase your soft skills in the interview to get jobs.</span>
      </p>
    `,
  },
  'Measurable Results': {
    title: 'Measurable Results',
    message: `
    <p>Recruiters scanning resumes typically notice and hone in on definitive results, shown in numbers. 
    The more specific you can be (while remaining concise), the better- add dates, length of time, and accurate numbers whenever possible.</p>
    <h5>Examples</h5>
    <ul>
      <li>"Supervised up to 25 employees..."</li>
      <li>"Reduced labor costs by more than 15%..."</li>
      <li>"Increased sales from $1.2 to $2.5 million in 2017..."</li>
      <li>"Ranked in top 5% of employee performance for 3 years running..."</li>
    </ul>
    <h5>Measurable result categories</h5>
    <strong>Money</strong>
    <ul>
      <li>sales volume</li>
      <li>number of units sold</li>
      <li>dollars and percentages with revenue</li>
      <li>share holder value increase</li>
      <li>cost savings</li>
      <li>budgets or funds served</li>
    </ul>
    <strong>People</strong>
    <ul>
      <li>numbers of customers served</li>
      <li>number of customers retained</li>
      <li>number of new customers</li>
      <li>numbers of direct reports</li>
      <li>number of people managed or hired</li>
      <li>size of teams led</li>
    </ul>
    <strong>Tasks</strong>
    <ul>
      <li>numbers of any tasks done in great quantity</li>
      <li>efficiency increases</li>
      <li>process improvements</li>
      <li>time saved</li>
    </ul>
    <strong>Other</strong>
    <ul>
      <li>numbers of awards or publications</li>
    </ul>
    `,
  },
  'Job Level Match': {
    title: 'Job Level Match',
    message: `
    <p>We recommend you apply to jobs that align with your experience level.</p>
    <p>This check does not affect your match rate.</p>
    `,
  },
  'Resume Tone': {
    title: 'Negative words, phrases and sentiment',
    message: `
    <p>Hiring managers and recruiters prefer action words that define specific experiences, 
    skills and accomplishments rather than cliched phrases, buzzwords or over-aggressive tones.</p>
    <h5>Examples of negative words or phrases</h5>
    <ul>
      <li><strong>Best of Breed</strong><div>Avoid cliches that does not bring value - leave the dog at home</div></li>
      <li><strong>Synergy</strong><div>Avoid phony business jargon. Use more concrete language to convey your point more effectively.</div></li>
      <li><strong>Results driven</strong><div>Avoid vague self-appointed adjectives. Instead, show you’re results driven (or otherwise) with actual impressive results you achieved.</div></li>
      <li><strong>Aggressive close of deals</strong><div>Avoid phrases that are overly negative in tone</div></li>
    </ul>
    `,
  },
  References: {
    title: 'References',
    message: `
    <p>Having a resume section for references or stating “references available upon request” is an outdated practice. 
    Recruiters assume all candidates have references available and will ask for them once you’re further along in the interview process. 
    Reference calls are often one of the final steps in the hiring process.</p>
    `,
  },
  'Web Presence': {
    title: 'Web Presence and Brand',
    message: `
    <p>Having a resume section for references or stating “references available upon request” is an outdated practice. 
    Recruiters assume all candidates have references available and will ask for them once you’re further along in the interview process. 
    Reference calls are often one of the final steps in the hiring process.</p>
    <figure class="quote">
      <blockquote>"I almost always click through to a candidate's website or listed online profile. It's one of my favorite parts of recruiting."</blockquote>
      <figcaption>HR Manager</figcaption>
    </figure>
    <figure class="quote">
      <blockquote>"I wish more people would include URLs for [other] web presences. They provide a much richer view of the person."</blockquote>
      <figcaption>Senior Recruiter</figcaption>
    </figure>
    `,
  },
  'Font Check': {
    title: 'Font Check',
    message: `
    <p>Your font choice is important to recruiters at face value. It’s the first impression of your professionalism. 
    A common, easy-to-read font is the safest choice and consistency matters. To create emphasis, use bold or italic fonts.</p>
    `,
  },
  'Page Setup': {
    title: 'Page Setup',
    message: `
    <p>Your ATS-optimized resume should be printable and legible to readers. Use a standard letter size, and ensure that margins are between 0.5 and 1 inch.</p>
    <p>Avoid putting information in your document's header or footer, as these areas are not always parsed by ATS.</p>
    `,
  },
  Layout: {
    title: 'Layout',
    message: `
    <p>Use standard left alignment to format your resume text, and try to organize your content into concise sentences and paragraphs.</p>
    <p>Avoid using columns, tables, or images in the content of your resume. These formatting tools can trip up ATS parsers.</p>
    `,
  },
  Summary: {
    title: 'Summary',
    message: `
    <p>The summary provides a quick overview of the candidate's qualifications, helping recruiters and hiring managers promptly grasp the value the candidate can offer in the position.</p>
    `,
  },
  'Paragraph Length': {
    title: 'Paragraph Length',
    message:
      '<p>We recommend keeping paragraphs concise to enhance readability and make it easier for recruiters to focus on your skills and accomplishments.</p>',
  },
}
